import { decodeFronteggAccessToken } from '@dtx-company/ithaca-sdk/src/frontegg/decodeFronteggAccessToken'
import { useAuthState } from '@dtx-company/inter-app/src/hooks/useAuthState'
import { useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import sendVerificationEmailService from '../../services/sendVerificationEmail'

interface UseResendVerificationEmailReturn {
  sendVerificationEmail: (() => void) | undefined
}
type UseResendVerificationEmail = () => UseResendVerificationEmailReturn

const useResendVerificationEmail: UseResendVerificationEmail = () => {
  const { token } = useAuthState()
  const { route } = useRouter()
  const [wasEmailSent, setWasEmailSent] = useState<boolean | undefined>(undefined)

  /**
   * Do not provide email to resend service if already verified
   *  Prevents user from setting ?emailVerificationResult=FAILURE then
   * spamming the resend button
   */
  const validEmail = useMemo(() => {
    if (token) {
      const decodedToken = decodeFronteggAccessToken(token)

      const isValidEmail = Boolean(decodedToken?.email.length)

      if (isValidEmail && !decodedToken?.email_verified) return decodedToken?.email
    }

    return undefined
  }, [token])

  const { isIdle, isSending, isComplete } = useMemo(() => {
    let isIdle = true
    let isSending = false
    let isComplete = false

    if (validEmail) {
      isIdle = wasEmailSent === undefined
      isSending = wasEmailSent === false
      isComplete = wasEmailSent === true
    }

    return { isIdle, isSending, isComplete }
  }, [validEmail, wasEmailSent])

  const setIdle = (): void => {
    setWasEmailSent(undefined)
  }
  const setSending = (): void => {
    setWasEmailSent(false)
  }
  const setComplete = (): void => {
    setWasEmailSent(true)
  }

  useEffect(() => {
    if (isSending && validEmail) {
      const isSendingEffect = async (): Promise<void> => {
        const res = await sendVerificationEmailService({ email: validEmail })

        if (res) {
          setComplete()
        } else {
          setIdle()
        }
      }
      isSendingEffect()
    }
  }, [validEmail, isSending])

  useEffect(() => {
    if (isComplete) {
      setIdle()
    }
  }, [isComplete, route])

  const sendVerificationEmail = isIdle ? setSending : undefined

  return {
    sendVerificationEmail
  }
}

export default useResendVerificationEmail
